import { FC } from 'react';
import { ActionMenu } from 'ui/actionMenu';
import { ActionMenuOption, AllowedActionMenuElements, MENU_ALIGNMENTS } from 'ui/actionMenu/types';
import { BUTTON_VARIANTS, IconButton } from 'ui/button';
import { Overflow } from 'ui/icons/navigation/Overflow';
import { Delete16 } from 'ui/icons/aurora/16px/Delete16';
import { Edit16 } from 'ui/icons/action/Edit16';
import { Favorite16 } from 'ui/icons/aurora/16px/Favorite16';
import { MODAL_NAMES } from 'modules/projectSettings/contexts';
import { ProjectLocales } from './LocalesTable';
import { useIsWhatsAppInfobipLocaleUsed } from 'modules/projectSettings/hooks';
import { useDispatch } from 'react-redux';
import { addNegativeToast } from 'modules/toastContainer';
import { CircleSpinner } from 'ui/spinner';

export enum MENU_ACTION_TYPES {
  EDIT = 'edit',
  DELETE = 'delete',
}

export const ActionMenuTableCell: FC<{ row: ProjectLocales }> = ({ row }) => {
  const dispatch = useDispatch();
  const { refetch, isLoading } = useIsWhatsAppInfobipLocaleUsed({
    variables: { locale: row.locale },
    skip: true,
  });

  const handleActionResult = (actionType: MENU_ACTION_TYPES, isLocaleUsed: boolean) => {
    if (isLocaleUsed) {
      dispatch(
        addNegativeToast(
          `This locale can't be ${actionType === MENU_ACTION_TYPES.EDIT ? 'edited' : 'deleted'} because it's used in at least one template.`,
        ),
      );
      return;
    }

    if (actionType === MENU_ACTION_TYPES.EDIT) {
      row.showModal({
        name: MODAL_NAMES.WA_LOCALE,
        payload: {
          locale: row.locale,
          language: row.language,
        },
      });
    } else if (actionType === MENU_ACTION_TYPES.DELETE) {
      row.showModal({ name: MODAL_NAMES.DELETE_LOCALE, payload: row.locale });
    }
  };

  const handleAction = (actionType: MENU_ACTION_TYPES) => {
    refetch()
      .then(({ data }) => {
        if (!!data) {
          handleActionResult(actionType, data.isWhatsAppInfobipLocaleUsed);
        }
      })
      .catch(() => {
        dispatch(
          addNegativeToast('Unable to check if the locale is used in templates. Please try again.'),
        );
      });
  };

  const baseOverflowOptions: ActionMenuOption[] = [
    {
      as: AllowedActionMenuElements.Button,
      content: (
        <>
          <Edit16 />
          Edit locale
        </>
      ),
      id: 'edit',
      onClick: () => handleAction(MENU_ACTION_TYPES.EDIT),
    },
    {
      as: AllowedActionMenuElements.Button,
      isDestructive: true,
      content: (
        <>
          <Delete16 />
          Delete locale
        </>
      ),
      id: 'delete',
      onClick: () => handleAction(MENU_ACTION_TYPES.DELETE),
    },
  ];

  const setDefaultOptions: ActionMenuOption = {
    as: AllowedActionMenuElements.Button,
    content: (
      <>
        <Favorite16 />
        <span>Set as default</span>
      </>
    ),
    id: 'default',
    onClick: () => row.showModal({ name: MODAL_NAMES.SET_DEFAULT_LOCALE, payload: row.locale }),
  };

  const overflowOptions = !row.isDefault
    ? [setDefaultOptions, ...baseOverflowOptions]
    : baseOverflowOptions;

  return (
    <ActionMenu options={overflowOptions} align={MENU_ALIGNMENTS.RIGHT} isDisabled={isLoading}>
      <IconButton variant={BUTTON_VARIANTS.GHOST}>
        {isLoading ? <CircleSpinner size={16} /> : <Overflow />}
      </IconButton>
    </ActionMenu>
  );
};
